import { dayjs } from '@/plugins/dayjs';
import { mapObjectToQueryParams } from '@/helpers/formatArrayParams';
import { add, append, prepend, set } from '@/vuex/helpers';
import i18n from '@/plugins/i18n';

export default {
  namespaced: true,
  state: {
    threads: [],
    newlyCreatedThreads: [],
    messages: [],
    family: [],
    messagingUsers: [],
    moreThreadsUrl: null,
    moreMessagesUrl: null,
    openedThreadData: {
      foreignerId: null,
      id: 'all',
    },
    foreigner: null,
    permits: [],
    notSentMessages: {},
    filters: {
      user_from: null,
      permit: null,
      date_create_from: null,
      date_create_to: null,
      body_search: null,
    },
    fulltextSearchValue: '',
    fulltextSearchResults: [],
    fulltextMessageId: null,
  },
  mutations: {
    set,
    add,
    append,
    prepend,
    updateFilter(state, { field, payload }) {
      state.filters[field] = payload;
      if (field !== 'page') {
        state.filters.page = 1;
      }
    },
    updateMessage(state, { messageId, payload }) {
      state.messages = state.messages.map((item) => {
        if (item.id === messageId) {
          item = { ...item, ...payload };
        }

        return item;
      });
    },
    updateNotSentMessages(state, { message, threadId, foreignerId }) {
      if (state.openedThreadData.id === 'all') return;

      if (!state.notSentMessages[foreignerId]) {
        state.notSentMessages[foreignerId] = {};
      }

      if (!state.notSentMessages[foreignerId][threadId]) {
        state.notSentMessages[foreignerId][threadId] = message;
      } else {
        state.notSentMessages = {
          ...state.notSentMessages,
          [foreignerId]: {
            ...state.notSentMessages[foreignerId],
            [threadId]: message,
          },
        };
      }
    },
  },
  actions: {
    openNewThread({ state, commit }, { permit, foreigner }) {
      const nullFreeForeigner = foreigner ?? state.foreigner;
      commit('add', ['newlyCreatedThreads', {
        permit_id: permit.id,
        seen: true,
        subject: permit.permit_type_name,
        unseen_messages: 0,
        reference_number: permit.reference_number,
        foreigner_id: nullFreeForeigner.id,
        foreigner_fullname: nullFreeForeigner.fullname,
        foreigner_avatar_url: nullFreeForeigner.avatar_url,
      }]);
      commit('set', ['openedThreadData', { id: String(permit.id), foreignerId: nullFreeForeigner.id }]);
    },
    async loadThreads({ state, commit }, foreignerId) {
      const response = await this.$axios.get(`/api_foreigner/${foreignerId}/message/preview/`);

      const filteredNewlyCreatedThreads = state.newlyCreatedThreads.filter(
        (newlyCreatedThread) => {
          const fetchedThreadsAlreadyHasNewlyCreated = response.data.data.some((item) => item.permit_id === newlyCreatedThread.permit_id);
          const isSameForeignerNewThread = foreignerId === newlyCreatedThread.foreigner_id;
          return isSameForeignerNewThread && !fetchedThreadsAlreadyHasNewlyCreated;
        },
      );

      commit('set', ['threads', response.data.data]);
      commit('set', ['moreThreadsUrl', response.data.next]);
      commit('set', ['newlyCreatedThreads', filteredNewlyCreatedThreads]);
    },

    async loadMoreThreads({ state, commit }) {
      const response = await this.$axios.get(state.moreThreadsUrl);

      commit('set', ['moreThreadsUrl', response.data.next]);
      commit('prepend', ['threads', response.data.data]);
    },

    async loadMessages({ state, commit }, { foreignerId, permitId, messageId }) {
      const response = await this.$axios.get(`/api_foreigner/${foreignerId}/message/`, {
        params: {
          page_size: state.fulltextMessageId || messageId ? -1 : 20,
          ... mapObjectToQueryParams(state.filters),
          permit: state.filters.permit ?? (permitId === 'all' ? undefined : permitId),
        },
      });

      commit('set', ['moreMessagesUrl', response.data.next]);
      commit('set', ['messages',  response.data.data.reverse()]);
    },

    async loadMoreMessages({ state, commit }) {
      const response = await this.$axios.get(state.moreMessagesUrl);

      commit('set', ['moreMessagesUrl', response.data.next]);
      commit('append', ['messages',  response.data.data.reverse()]);
    },

    async patchSeenMessages({ commit }, { foreignerId, unseenMessagesIds }) {
      await this.$axios.$patch(`/api_foreigner/${foreignerId}/message/`, {
        messages: unseenMessagesIds,
      });
    },

    async loadFamilyMessagesCount({ commit }) {
      const items = await this.$axios.$get('/api_foreigner/message');

      commit('set', ['family', items]);
    },

    async changeMessagePermit({ commit }, { foreignerId, messageId, permitId }) {
      const payload = {
        permit_id: permitId,
      };
      await this.$axios.$patch(`/api_foreigner/${foreignerId}/message/${messageId}/`, payload);

      commit('updateMessage', {
        messageId,
        payload,
      });
    },

    async loadMessagingUsers({ commit }) {
      const response = await this.$axios.$get('/api_foreigner/message/user/');

      commit('set', ['messagingUsers',  response]);
    },

    async fulltextSearch({ state, commit }, { foreignerId }) {
      const response = await this.$axios.$get(`/api_foreigner/${foreignerId}/message/preview/`, {
        params: {
          body_search: state.fulltextSearchValue,
        },
      });

      commit('set', ['fulltextSearchResults',  response]);
    },
    async loadForeignerDetail({ commit }, id) {
      const foreigner = await this.$axios.$get(`/api_foreigner/${id}/`);
      commit('set', ['foreigner', foreigner]);
    },
    async loadForeignerPermits({ commit }, foreignerId) {
      const permits = await this.$axios.$get(`/api_foreigner/${foreignerId}/permit/`);
      commit('set', ['permits', permits]);
    },

    async sendMessage({ state, commit }, { text, permit_id, recipients }) {
      const foreignerId = state.foreigner.id;

      const message = await this.$axios.$post(`/api_foreigner/${foreignerId}/message/`, {
        body: text,
        permit_id: permit_id,
        recipients: recipients,
      });
      commit('prepend', ['messages', [message]]);
    },
  },

  getters: {
    getMessages(state) {
      let isFirstUnseen = false;
      let isFirstToday = false;

      const messages = state.messages.map((message) => {
        if(!message.seen && !isFirstUnseen) {
          message.isFirstUnseen = true;
          isFirstUnseen = true;
        } else {
          message.isFirstUnseen = false;
        }

        if (dayjs(message.date_created, 'DD.MM.YYYY HH:mm:ss').isSame(dayjs(), 'day') && !isFirstToday) {
          message.isFirstToday = true;
          isFirstToday = true;
        }

        return message;
      });

      return messages;
    },
    getUnseenMessagesIds(state, getters) {
      return getters.getMessages.filter((message) => !message.seen).map(({ id }) => id);
    },
    getOpenedThreadPermit(state) {
      return state.threads.find((thread) => String(thread.permit_id) === String(state.openedThreadData.id) && String(thread.foreigner_id) === String(state.openedThreadData.foreignerId));
    },
    threads(state) {
      return [...state.newlyCreatedThreads, ...state.threads];
    },
    canBeOpenedThreads(state) {
      const result = state.permits.filter((permit) => !state.threads.some((thread) => thread.permit_id === permit.id));
      if (!state.threads.some((thread) => thread.permit_id == null)) {
        result.push({
          id: null,
          permit_type_name: capitalize(i18n.global.t('messaging.subject.other')),
        });
      }
      return result;
    },
  },
};
